import { ServiceLanguageMap } from "@albertine/i18n";
import {
    BillingPeriodId,
    BillingPeriodIdMap,
    defaultCommitmentPeriods,
    defaultTrialPeriods,
    isNoTrial,
} from "@albertine/types";
import {
    type MembershipTier,
    type Tenant,
    TenantSchema,
} from "@albertine/types/openapi/lmt";
import { tenYearsEndMonth } from "@prototypes/date-time-picker/DateTimePicker";
import { FormSectionLabel } from "@prototypes/form-field/FormField";
import { DateField, OwnedField } from "@prototypes/member-profile/overlays/FormFields";
import { isValid } from "date-fns";
import { type UseFormReturn, useWatch } from "react-hook-form";
import { formatTenantName } from "../../common/utils/tenant.util";
import {
    membershipChoicesForTenant,
    membershipOptionsForTenant,
    type NewMemberFormInput,
    type NewMemberFormOutput,
    paidMembershipEndDate,
} from "./newMember.util";

const trialOptions = defaultTrialPeriods
    .filter((period) => period.active)
    .map((period) => ({ id: period.id, label: period.name }));

const commitmentPeriodOptions = defaultCommitmentPeriods
    .filter((period) => period.active)
    .map((period) => ({ id: period.id, label: period.name }));

const billingPeriodOptions = BillingPeriodId.options.map((period) => ({
    id: period,
    label: BillingPeriodIdMap[period],
}));

export function NewMemberMembershipFields({
    form,
    tenantOptions,
    membershipTiers,
}: {
    form: UseFormReturn<NewMemberFormInput, unknown, NewMemberFormOutput>;
    tenantOptions: Tenant[];
    membershipTiers: MembershipTier[];
}) {
    const { control, getValues, setValue } = form;
    const [tenant, trial, startDate, commitmentPeriod] = useWatch({
        control,
        name: ["tenant", "trial", "startDate", "commitmentPeriod"],
    });
    const { tiers, languages, trialsEnabled } = membershipOptionsForTenant(
        tenant,
        membershipTiers,
    );
    const endDate =
        startDate && isValid(startDate)
            ? paidMembershipEndDate(startDate, commitmentPeriod)
            : undefined;

    // The tenant from this render is the one being switched away from.
    function switchTenant(next: string) {
        const nextTenant = TenantSchema.safeParse(next);
        if (!nextTenant.success) return;

        const choices = membershipChoicesForTenant(
            { ...getValues(), tenant },
            nextTenant.data,
            membershipTiers,
        );
        // TODO: Replace these with a single setValues call once react-hook-form is
        // upgraded to 7.74.0 or newer, where it was introduced.
        setValue("tier", choices.tier);
        setValue("trial", choices.trial);
        setValue("serviceLanguage", choices.serviceLanguage);
    }

    return (
        <>
            <FormSectionLabel className="mt-0 mb-0">Membership</FormSectionLabel>

            <div className="grid items-start gap-4 sm:grid-cols-2">
                <OwnedField
                    id="new-member-tenant"
                    label="Tenant"
                    kind="select"
                    options={tenantOptions.map((option) => ({
                        id: option,
                        label: formatTenantName(option),
                    }))}
                    control={control}
                    name="tenant"
                    onValueChange={switchTenant}
                />
                <OwnedField
                    id="new-member-tier"
                    label="Tier"
                    kind="select"
                    options={tiers.map((tier) => ({ id: tier.id, label: tier.name }))}
                    control={control}
                    name="tier"
                    placeholder="No tier"
                    disabled={tiers.length <= 1}
                />
            </div>

            <div className="grid items-start gap-4 sm:grid-cols-2">
                <OwnedField
                    id="new-member-trial"
                    label="Trial duration"
                    kind="select"
                    options={trialOptions}
                    control={control}
                    name="trial"
                    disabled={!trialsEnabled}
                />
                <OwnedField
                    id="new-member-language"
                    label="Language"
                    kind="select"
                    options={languages.map((language) => ({
                        id: language,
                        label: ServiceLanguageMap[language],
                    }))}
                    control={control}
                    name="serviceLanguage"
                    disabled={languages.length <= 1}
                />
            </div>

            {isNoTrial(trial) && (
                <>
                    <div className="grid items-start gap-4 sm:grid-cols-2">
                        <OwnedField
                            id="new-member-commitment-period"
                            label="Commitment period"
                            kind="select"
                            options={commitmentPeriodOptions}
                            control={control}
                            name="commitmentPeriod"
                        />
                        <OwnedField
                            id="new-member-billing-period"
                            label="Billing period"
                            kind="select"
                            options={billingPeriodOptions}
                            control={control}
                            name="billingPeriod"
                        />
                    </div>

                    <div className="grid items-start gap-4 sm:grid-cols-2">
                        <OwnedField
                            id="new-member-start-date"
                            label="Membership start date"
                            kind="date"
                            control={control}
                            name="startDate"
                            endMonth={tenYearsEndMonth()}
                        />
                        <DateField
                            id="new-member-end-date"
                            label="Membership renewal date"
                            date={endDate}
                            disabled
                        />
                    </div>
                </>
            )}
        </>
    );
}
